import { useQuery } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { isValidScale } from './credits';
import { LIVE, isUnavailable } from './live';

export type ScaleStatus = 'ok' | 'loading' | 'unavailable' | 'forbidden' | 'error';

/**
 * Billing's live `credit_display_scale` for an environment (`GET …/billing/settings`, billing:read).
 *
 * Used to DISPLAY micro-credits and to CONVERT an admin-typed displayed amount into micro-credits before
 * a grant. It is never guessed: when billing is down or the role cannot read its settings, the status
 * says so and the caller shows raw micro-credits / disables the input (docs/api-tenant-ops.md §4.5).
 */
export function useCreditScale(env: string | null): {
  scale: number | null;
  status: ScaleStatus;
  error: unknown;
} {
  const allowed = hasPermission(PERMISSIONS.billingRead);
  const query = useQuery({
    queryKey: ['ops', 'billing', env, 'settings'],
    queryFn: () => billingApi.settings(env!),
    enabled: !!env && allowed,
    ...LIVE,
  });

  if (!allowed) return { scale: null, status: 'forbidden', error: null };
  if (!env || query.isPending) return { scale: null, status: 'loading', error: null };
  if (query.isError) {
    return { scale: null, status: isUnavailable(query.error) ? 'unavailable' : 'error', error: query.error };
  }
  const scale = query.data?.credit_display_scale;
  return isValidScale(scale) ? { scale, status: 'ok', error: null } : { scale: null, status: 'error', error: null };
}

/** Why a displayed-credit input is disabled, in words, for a non-ok scale status. */
export function scaleUnavailableReason(status: ScaleStatus): string | null {
  switch (status) {
    case 'ok':
      return null;
    case 'loading':
      return 'Reading the credit display scale from billing…';
    case 'forbidden':
      return 'Your role cannot read billing settings, so the credit display scale is unknown.';
    case 'unavailable':
      return 'Billing is unavailable, so the credit display scale cannot be read — amounts are never converted with a guessed scale.';
    default:
      return 'The credit display scale could not be read from billing.';
  }
}
